import { inject, PLATFORM_ID } from '@angular/core';
import { type CanActivateFn, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { StorageService } from '../services/storage.service';
import { StorageEnum } from '../enums/storage.enum';
import { Nav } from '../enums/nav.enum';
import { createDeepLinkPayload } from '../models/deep-link-payload.model';

const DIGITAL_PAYMENT_CALLBACK_PATH = '/digital-payment-callback';

/**
 * Deep link URL pattern mappings to internal navigation routes.
 * Maps external deep link paths to their corresponding Nav enum values.
 */
const DEEP_LINK_MAPPINGS: Array<{ pattern: string; destination: Nav | string }> = [
  { pattern: '/payments/gateway/makepayment', destination: Nav.Payment },
  { pattern: '/accounts/manageaccounts?source=notifications', destination: Nav.ManageAccount + '/alerts' },
  { pattern: '/accounts/manageaccounts?source=referFriend', destination: Nav.ManageAccount + '/refer-friend' },
  { pattern: '/accounts/referfriend/getcode', destination: Nav.ManageAccount + '/refer-friend' },
  { pattern: '/payments/paymentdetail', destination: Nav.BillingHistory },
  { pattern: '/accounts/manageaccounts', destination: Nav.ManageAccount + '/overview' },
  { pattern: '/manage-account/plan-details', destination: Nav.PlanDetails },
  { pattern: '/payments/autopay-enroll', destination: Nav.AutopayEnroll },
];

/**
 * Functional guard that captures deep link URLs before the wildcard route redirects to login.
 * Stores the intended destination in session storage so the user is redirected after authentication.
 * @returns Always returns true to allow redirect to proceed
 */
export const deepLinkGuard: CanActivateFn = () => {
  const platformId = inject(PLATFORM_ID);
  const storage = inject(StorageService);
  const router = inject(Router);

  if (!isPlatformBrowser(platformId)) {
    return router.createUrlTree(['/login']);
  }

  const pathOnly = window.location.pathname + window.location.search;

  // Preserve callback payload for digital wallet flows (token/type/status query params)
  if (pathOnly.startsWith(DIGITAL_PAYMENT_CALLBACK_PATH)) {
    const returnRoute = getStoredReturnRoute(storage.getSession(StorageEnum.DigitalPaymentReturnRoute));
    const isAuthenticated =
      !isNullOrUndefined(storage.getSession(StorageEnum.LoginResponse)) ||
      !isNullOrUndefined(storage.getSession(StorageEnum.MultiAccountResponse));

    if (isAuthenticated) {
      storage.removeSession(StorageEnum.DigitalPaymentReturnRoute);
      return router.parseUrl(returnRoute ?? Nav.AccountSummary);
    }

    storage.setSession(
      StorageEnum.DeepLink,
      createDeepLinkPayload(returnRoute ?? pathOnly, 'digital-payment-callback'),
    );
    if (returnRoute) {
      storage.removeSession(StorageEnum.DigitalPaymentReturnRoute);
    }
    storage.hasDeepLinkRedirect.set(true);
    return router.createUrlTree(['/login']);
  }

  // Check if the current path matches any known deep link patterns
  for (const mapping of DEEP_LINK_MAPPINGS) {
    if (pathOnly.includes(mapping.pattern)) {
      storage.setSession(StorageEnum.DeepLink, createDeepLinkPayload(mapping.destination, 'legacy-mapping'));
      storage.hasDeepLinkRedirect.set(true);
      break;
    }
  }

  // Redirect to login (guard allows the navigation to proceed with the redirect)
  return router.createUrlTree(['/login']);
};

function getStoredReturnRoute(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const trimmedRoute = value.trim();
  return trimmedRoute.startsWith('/') ? trimmedRoute : `/${trimmedRoute}`;
}

function isNullOrUndefined(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}
